import React from "react";
import { Flex, Text, Box, type BoxProps } from "@chakra-ui/react";
import { Logo } from "components/brand/logo";

type BookCoverProps = {
  isOpen: boolean;
  title: string;
  colorScheme: "green" | "blue";
  width?: BoxProps["width"];
  height?: BoxProps["height"];
};

/**
 *
 * 3D Book with hover animation.
 * @source inspired from https://vercel.com/resources
 */
export function BookCover({
  isOpen,
  title,
  colorScheme,
  width = { base: "220px", md: "280px" },
  height = { base: "252px", md: "340px" },
}: BookCoverProps) {
  return (
    <Box width={width} height={height} userSelect="none">
      <Box
        boxShadow="md"
        width="var(--book-width)"
        height="var(--book-height)"
        pos="relative"
        sx={{
          perspective: "900px",
          "--book-width": "100%",
          "--book-height": "100%",
          "--book-depth": "54px",
          "--book-border-radius": "var(--chakra-radii-tinier)",
          "--book-cover-transform": "rotateY(-18deg) translateX(-1em) scale(1.05)",
        }}
      >
        {/* Book container, this will rotate outwards as if the book is opening on hover */}
        <Box
          boxSize="100%"
          borderRadius="var(--book-border-radius)"
          pos="relative"
          transition="transform 0.15s ease-out"
          transform={{ base: "var(--book-cover-transform)", md: isOpen ? "var(--book-cover-transform)" : "none" }}
          sx={{
            transformStyle: "preserve-3d",
          }}
        >
          {/* Book cover */}
          <Flex
            pos="absolute"
            inset="0"
            bgGradient={`linear(45deg, new.${colorScheme}.900, new.${colorScheme}.500 120%)`}
            borderRadius="var(--book-border-radius)"
            align="stretch"
            pl="bigger"
            pr="big"
            py="big"
          >
            <Flex flexDir="column" justify="space-between" gap="small">
              <Logo width="72px" fill={`new.${colorScheme}.50`} />
              <Text textStyle={{ base: "caption1", md: "body3" }} fontWeight="semibold" color="white">
                {title}
              </Text>
            </Flex>
          </Flex>
          {/* Spine */}
          <Box
            pos="absolute"
            left="0"
            top="0"
            bottom="0"
            width="smaller"
            bg={`new.${colorScheme}.900`}
            borderLeftRadius="var(--book-border-radius)"
            opacity={0.3}
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "6px",
              bgGradient: `linear(to-r, new.${colorScheme}.950 -20%, new.${colorScheme}.900 100%)`,
              zIndex: 1,
            }}
            _after={{
              content: '""',
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: "6px",
              bgGradient: `linear(to-l, new.${colorScheme}.950 -20%, new.${colorScheme}.900 100%)`,
              zIndex: 1,
            }}
          />

          {/* Inside pages */}
          <Box
            pos="absolute"
            right="0"
            top="4px"
            bottom="4px"
            width="48px"
            bgGradient="linear(90deg, brand.background.light 0%, brand.backgroundSecondary.light 100%)"
            transform="translateX(calc(var(--book-width) - var(--book-depth) / 2 - 0.5px)) rotateY(90deg) translateX(calc(var(--book-depth) / 2))"
          />
          {/* Back cover */}
          <Box
            pos="absolute"
            inset="0"
            bg={`new.${colorScheme}.950`}
            borderRadius="var(--book-border-radius)"
            transform="translateZ(calc(-1 * var(--book-depth)))"
          />
        </Box>
      </Box>
    </Box>
  );
}
